import { FC, useEffect } from 'react';
import { Controls, VideoPlayer } from '@deaf-bible-society-2-0/deafbible-video-player';

import { RecordItem } from './types';
import styles from './styles.module.css';

interface BBBResourceRightColumnProps {
  resource: RecordItem;
  isMobileDevice: boolean;
  isDarkMode: boolean;
}

const BBBResourceRightColumn: FC<BBBResourceRightColumnProps> = ({
  resource,
  isMobileDevice,
  isDarkMode,
}) => {
  const videoSource =
    resource.media === 'video' && /iPhone|iPad|iPod|Safari/i.test(navigator.userAgent)
      ? resource.videoUrl?.hls
      : resource.videoUrl?.dash;

  useEffect(() => {
    // Remove default video player overlay
    const removeOverlay = () => {
      const overlay = document.querySelector('div[role="button"]._container_1c0dc_1');
      if (overlay) {
        overlay.remove();
      }
    };

    // Run on mount and whenever videoSource changes
    removeOverlay();
    const timer = setTimeout(removeOverlay, 100); // Add slight delay to ensure overlay is loaded

    return () => clearTimeout(timer);
  }, [resource]);

  const getImageContainerClass = () => {
    if (isMobileDevice) return styles.imageContainerFullWidth;

    if (resource.media === 'video') {
      return !resource.contextualText.title ? styles.videoContainerWithoutText : '';
    }

    if (resource.media === 'image') {
      return !resource.contextualText.title
        ? styles.imageContainerWithoutText
        : styles.imageContainer;
    }

    return '';
  };

  const descriptionTitleCss = `${styles.descriptionTitle} ${
    !isDarkMode && styles.descriptionTitleDarkMode
  }`;

  const descriptionSubtitleCss = `${styles.descriptionSubtitle} ${
    !isDarkMode && styles.descriptionSubtitleDarkMode
  }`;

  const descriptionDescriptionCss = `${styles.descriptionDescription} ${
    !isDarkMode && styles.descriptionDescriptionDarkMode
  }`;

  return (
    <div
      className={`${styles.rightColumnWrapper} ${
        resource.media === 'text' ? styles.rightColumnWrapperOnlyText : ''
      }`}
    >
      {resource.media !== 'text' && (
        <div className={styles.topRow}>
          <div
            className={`${styles.videoContainer} ${getImageContainerClass()}`}
            style={{
              ...(isMobileDevice ? { padding: '0 0' } : {}),
              ...(resource.media === 'video' ? { width: '100%' } : {}),
            }}
          >
            {videoSource && resource.media === 'video' && typeof videoSource === 'string' && (
              <VideoPlayer
                src={videoSource}
                autoplay={false}
                controls={
                  (window.innerWidth >= 800 && window.innerWidth <= 950) ||
                  /iPad|Android|Tablet/.test(navigator.userAgent)
                    ? Controls.partialControlBar
                    : undefined
                }
              />
            )}
            {resource.imageUrl && resource.media === 'image' && (
              <div className={`${styles.imageContainer} ${getImageContainerClass()}`}>
                <img
                  src={resource.imageUrl}
                  alt={resource.contextualText.title || ''}
                  className={styles.imageWrapper}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className={`${styles.bottomRow} ${!isDarkMode && styles.bottomRowDarkMode} ${
          isMobileDevice && styles.bottomRowMobile
        }`}
        style={isMobileDevice ? { flex: 3 } : undefined}
      >
        <div
          style={isMobileDevice ? { paddingTop: '10px' } : undefined}
          className={styles.descriptionContainer}
        >
          <div
            className={descriptionTitleCss}
            style={{
              fontSize: isMobileDevice ? '16px' : '20px',
            }}
          >
            {resource.contextualText.title}
          </div>
          <div
            className={descriptionSubtitleCss}
            style={{
              fontSize: isMobileDevice ? '14px' : '16px',
            }}
          >
            {resource.contextualText.subtitle}
          </div>
          <div
            className={descriptionDescriptionCss}
            style={{
              fontSize: isMobileDevice ? '12px' : '14px',
            }}
          >
            {resource.contextualText.description}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BBBResourceRightColumn;
